import { CONTENT_TYPE } from "../../app/constants";
import {
  RecommendationsCloseIcon,
  RecommendationsNextIcon,
  RecommendationsPrevIcon,
} from "../../assets/icons/RecommendationsIcons";
import "./Recommendations.css";
import { motion } from "framer-motion";
import gluedin from "gluedin-shorts-js";
import React, {
  useEffect,
  useRef,
  useState,
  useRef as useRefAlias,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

// Ensure you have the correct CSS file

const SvodModule = new gluedin.GluedInSvodModule();

export const Recommendations = ({
  labels,
  onClose,
}: {
  labels?: string[] | string | null;
  onClose: () => void;
}) => {
  const [recommendations, setRecommendations] = useState<any[]>([]);
  const [offset, setOffset] = useState(1); // page-based pagination
  const limit = 10;
  const [hasMore, setHasMore] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [isInitialFetchResolved, setIsInitialFetchResolved] = useState(false);
  const [hasInitialData, setHasInitialData] = useState(false);
  const listRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleOpenSeries = (s: any) => {
    const contentRoute = localStorage.getItem("contentRoute");
    // const base = contentRoute === "discover" ? "/discover" : "";
    window.location.href = `/series/${s?.seriesId}?&offset=1&contentType=${CONTENT_TYPE.SERIES}`;
    // navigate(`/series/${s.seriesId}?contentType=series&offset=1`);
    onClose(); // close popup after navigating
  };

  // Build params WITHOUT `label` if it's null/undefined/empty
  const buildRecommendationParams = (
    page: number,
    size: number,
    rawLabels?: string[] | string | null
  ): { offset: number; limit: number } & { label?: string } => {
    const params: { offset: number; limit: number; label?: string } = {
      offset: page,
      limit: size,
    };

    if (typeof rawLabels === "string" && rawLabels.trim()) {
      params.label = rawLabels.trim();
    } else if (Array.isArray(rawLabels)) {
      const joined = rawLabels
        .filter((s) => typeof s === "string" && s.trim().length > 0)
        .join(",");
      if (joined) params.label = joined;
    }

    return params;
  };

  // Sequence guard to avoid racing/double-apply in StrictMode/dev
  const reqSeqRef = useRefAlias(0);

  const fetchRecommendations = async (
    page: number = offset,
    isInitialFetch: boolean = false
  ) => {
    if (isFetching) return;
    if (!isInitialFetch && !hasMore) return;

    setIsFetching(true);
    const mySeq = ++reqSeqRef.current;

    try {
      const params = buildRecommendationParams(page, limit, labels);
      const response = await SvodModule.getRecommendationSeries(params);
      const result = Array.isArray(response?.data?.result)
        ? response.data.result
        : [];

      if (mySeq !== reqSeqRef.current) return; // ignore stale

      setRecommendations((prev) => {
        const existingIds = new Set(prev.map((s) => s.seriesId));
        const filtered = result.filter(
          (s: any) => !existingIds.has(s.seriesId)
        );
        return [...prev, ...filtered];
      });

      setHasMore(result.length >= limit);
      setOffset(page + 1);

      if (isInitialFetch) {
        const receivedData = result.length > 0;
        setHasInitialData(receivedData);
        setIsInitialFetchResolved(true);
        if (!receivedData) {
          onClose();
        }
      }
    } catch (err) {
      console.error("Failed to fetch recommendations:", err);
      if (isInitialFetch) {
        setHasInitialData(false);
        setIsInitialFetchResolved(true);
        onClose();
      }
    } finally {
      if (mySeq === reqSeqRef.current) setIsFetching(false);
    }
  };

  // Reset when labels change
  useEffect(() => {
    reqSeqRef.current++; // invalidate in-flight requests
    setRecommendations([]);
    setOffset(1);
    setHasMore(true);
    setIsFetching(false);
    setIsInitialFetchResolved(false);
    setHasInitialData(false);
    fetchRecommendations(1, true);
  }, [Array.isArray(labels) ? labels.join(",") : labels ?? ""]);

  // Drag end → load more when near end
  const handleDragEnd = () => {
    const slider = listRef.current;
    if (!slider) return;
    const nearEnd =
      slider.scrollWidth - slider.clientWidth - slider.scrollLeft < 50;
    if (nearEnd && hasMore) fetchRecommendations(offset, false);
  };

  // Buttons: small nudge per click; prefetch when close to end
  const STEP = 160; // card width + gap; adjust if your CSS changes
  const handleNext = () => {
    const el = listRef.current;
    if (!el) return;
    el.scrollBy({ left: STEP, behavior: "smooth" });

    const remaining = el.scrollWidth - el.clientWidth - el.scrollLeft;
    if (remaining < 2 * STEP && hasMore) fetchRecommendations(offset, false);
  };

  const handlePrev = () => {
    const el = listRef.current;
    if (!el) return;
    el.scrollBy({ left: -STEP, behavior: "smooth" });
  };

  if (!isInitialFetchResolved || !hasInitialData) {
    return null;
  }

  return (
    <motion.div
      className="recommendations-popup"
      initial={{ y: "100%", opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: "100%", opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <button className="recommendations-popup-close" onClick={onClose}>
        <RecommendationsCloseIcon />
      </button>
      <h3 className="recommendations-popup-title">
        {t("label-recommendation-title")}
      </h3>

      {/* Loader inside scroll */}
      <motion.div
        className="recommendations-popup-scroll"
        ref={listRef}
        drag="x"
        dragConstraints={{ left: -1000, right: 0 }}
        onDragEnd={handleDragEnd}
      >
        {recommendations.map((series) => (
          <motion.div
            key={series.seriesId}
            className="recommendations-popup-item"
            role="button"
            tabIndex={0}
            onTap={() => handleOpenSeries(series)} // tap/click without drag
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") handleOpenSeries(series);
            }}
            style={{ cursor: "pointer" }}
          >
            <img
              src={series.thumbnailUrl}
              alt={series.title}
              className="recommendations-popup-item-img"
            />
            <p>{series.title}</p>
          </motion.div>
        ))}

        {isFetching && recommendations.length > 0 && (
          <div className="recommendations-loader"></div>
        )}
      </motion.div>

      <div className="recommendations-controls">
        <button onClick={handlePrev} className="recommendations-controls-left">
          <RecommendationsPrevIcon />
        </button>
        <button
          onClick={handleNext}
          className="recommendations-controls-right"
          disabled={isFetching && !hasMore}
        >
          <RecommendationsNextIcon />
        </button>
      </div>
    </motion.div>
  );
};
